import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ChevronDown, ChevronRight } from "lucide-react";

import {
  addBriefItem,
  confirmBriefItem,
  getAccountBrief,
  rejectBriefItem,
  resolveBriefConflict,
  resolveBriefPromise,
  retractBriefItem,
  undoAutoBriefItem,
  verifyBriefItem,
} from "../../../api/accountBriefApi";
import { revealAccessSafetyFact } from "../../../api/accessSafety";
import "./AccountBriefCard.css";

// Account Brief — what the business knows about this account, and where it came
// from. Office audience: confirmed items, the "To confirm" list (AI suggestions,
// never shown to techs), conflicts, promises, access & safety, money (manager).
// Every decision carries shown_at so the server can measure rubber-stamping.

const SOURCE_WORD = {
  call: "call", text: "text", email: "email", tech_note: "tech note", fr_note: "FieldRoutes note",
  appointment: "appointment", observation: "photo", office: "office", tech_app: "tech at the stop",
  voice: "voice call", job_pool: "job-pool flag",
};

const SECTION_TITLE = {
  coverage: "Warranty & agreement",
  fact: "Facts & preferences",
  condition: "Conditions",
  schedule: "Scheduling",
  history: "History",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const sourceLine = (it) => {
  const s = it.source || {};
  const bits = [SOURCE_WORD[s.type] || s.type || "record"];
  if (s.ref) bits.push(s.ref.length > 14 ? `${s.ref.slice(0, 12)}…` : s.ref);
  if (s.at) bits.push(fmtDate(s.at));
  return bits.join(" · ");
};

// "last from a call" / "an email" / "the office" — the source as a phrase.
const FROM_PHRASE = { call: "a call", voicemail: "a voicemail", sms: "a text", text: "a text", email: "an email", note: "a note", office: "the office", tech: "a tech note", visit: "a visit", form: "a form", import: "an import" };
const fromPhrase = (type) => {
  const t = String(type || "").toLowerCase();
  if (FROM_PHRASE[t]) return FROM_PHRASE[t];
  const word = (SOURCE_WORD[type] || t || "a record").toLowerCase();
  return /^(a|an|the) /.test(word) ? word : `${/^[aeiou]/.test(word) ? "an" : "a"} ${word}`;
};

const OWNER_WORD = { tech_on_next_visit: "Tech on next visit", office: "Office" };
const ownerLabel = (o) => OWNER_WORD[o] || o || "Office";

const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

function useShownAt(items) {
  // first time each id rendered — the dwell clock the server needs
  const seen = useRef({});
  items.forEach((it) => {
    if (!seen.current[it.id]) seen.current[it.id] = new Date().toISOString();
  });
  return (id) => seen.current[id];
}

function Item({ it, children, tone }) {
  return (
    <div className={`ab-item ${tone ? `ab-item--${tone}` : ""}`}>
      {children}
    </div>
  );
}

// `collapsible` (the customer window): the brief sits collapsed under the Overview
// columns and opens by itself only when it has something in it.
export default function AccountBriefCard({ customerId, variant = "page", collapsible = false }) {
  const [open, setOpen] = useState(null);
  const [brief, setBrief] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState({});
  const [reasons, setReasons] = useState({});
  const [revealed, setRevealed] = useState({});
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ field_key: "", text: "" });

  const load = useCallback(async () => {
    if (!customerId) return;
    try {
      const { data } = await getAccountBrief(customerId, "office");
      setBrief(data);
      setError("");
    } catch (e) {
      setError(errText(e));
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const toConfirm = brief?.to_confirm || [];
  const conflicts = brief?.conflicts || [];
  const shownAt = useShownAt([...toConfirm, ...conflicts]);

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy("");
    }
  };

  const items = brief?.items || [];
  const hazards = items.filter((i) => i.klass === "hazard");
  const doNots = items.filter((i) => i.klass === "do_not");
  const promises = items.filter((i) => i.section === "promise");
  const rest = items.filter((i) => i.klass === "standard" && i.section !== "promise");
  const bySection = useMemo(() => {
    const out = {};
    rest.forEach((i) => { (out[i.section] = out[i.section] || []).push(i); });
    return out;
  }, [rest]);
  const fields = brief?.fields || [];
  const contentCount =
    items.length + toConfirm.length + conflicts.length + (brief?.access_safety || []).length;
  useEffect(() => {
    if (collapsible && brief && open === null) setOpen(false);
  }, [collapsible, brief, open, contentCount]);
  const collapsed = collapsible && !open;

  if (!customerId) return null;

  // The one-line form: "3 confirmed facts · last from a call on Oct 19".
  const confirmedItems = items.filter((i) => i.confirmed_at || i.confirmed_label);
  const latest = [...items].sort((a, b) => new Date(b.source?.at || b.confirmed_at || 0) - new Date(a.source?.at || a.confirmed_at || 0))[0];
  const oneLine = brief
    ? confirmedItems.length
      ? `${confirmedItems.length} confirmed ${confirmedItems.length === 1 ? "fact" : "facts"}${latest ? ` · last from ${fromPhrase(latest.source?.type)}${latest.source?.at || latest.confirmed_at ? ` on ${fmtDate(latest.source?.at || latest.confirmed_at)}` : ""}` : ""}${toConfirm.length + conflicts.length ? ` · ${toConfirm.length + conflicts.length} to confirm` : ""}`
      : toConfirm.length + conflicts.length
        ? `Nothing confirmed yet · ${toConfirm.length + conflicts.length} to confirm`
        : "Nothing confirmed yet"
    : "Loading…";
  if (collapsible && brief && !error && contentCount === 0) {
    return <p className="ab-line" data-testid="account-brief">Account brief: nothing confirmed yet. Suggestions from calls, texts and tech notes land here for you to confirm.</p>;
  }
  if (brief && brief.applicable === false) return null;

  const confirmedLine = (it) => (
    <span className="ab-src">
      {sourceLine(it)} · {it.confirmed_label || "confirmed"}
      {it.still_true_due ? <span className="ab-age"> · confirmed {fmtDate(it.confirmed_at)} — still true?</span> : null}
    </span>
  );

  const stillTrue = (it) => it.still_true_due ? (
    <div className="ab-actions">
      <button type="button" className="ab-btn ab-btn--ok" disabled={!!busy}
        onClick={() => run(`v${it.id}`, () => verifyBriefItem(it.id, { still_true: true }), "Re-verified")}>
        Still true
      </button>
      <button type="button" className="ab-btn" disabled={!!busy}
        onClick={() => {
          const reason = reasons[it.id];
          if (!reason) { toast.info("Say what changed, then tap No longer true."); setEditing({ ...editing, [`r${it.id}`]: true }); return; }
          run(`v${it.id}`, () => verifyBriefItem(it.id, { still_true: false, reason }), "Archived with your reason");
        }}>
        No longer true
      </button>
      {editing[`r${it.id}`] ? (
        <input id={`ab-reason-${it.id}`} className="ab-input" placeholder="What changed?"
          value={reasons[it.id] || ""} onChange={(e) => setReasons({ ...reasons, [it.id]: e.target.value })} />
      ) : null}
    </div>
  ) : null;

  return (
    <div className={variant === "popup" ? "cov-card ab-card ab-card--popup" : "cdp-summary-card ab-card"}
      data-testid="account-brief">
      {collapsible ? (
        <button type="button" className="cov-card__head ab-head ab-head--toggle"
          aria-expanded={!collapsed} onClick={() => setOpen(collapsed)}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
          <span>Account brief</span>
          <span className="ab-sub">{oneLine}</span>
        </button>
      ) : (
        <div className={variant === "popup" ? "cov-card__head ab-head" : "cdp-section-head ab-head"}>
          {variant === "popup" ? <span>Account brief</span> : <h3>Account brief</h3>}
          <span className="ab-sub">What we know, where it came from, who confirmed it</span>
        </div>
      )}

      {collapsed ? null : (<>

      {error ? <p className="ab-empty">Couldn't load the brief: {error}</p> : null}
      {!brief && !error ? <p className="ab-empty">Loading…</p> : null}

      {(toConfirm.length > 0 || conflicts.length > 0) && (
        <section className="ab-sec">
          <div className="ab-sec-head">
            <h4>To confirm</h4>
            <span className="ab-count">{toConfirm.length + conflicts.length} waiting · techs don't see these</span>
          </div>
          {conflicts.map((it) => {
            const old = items.find((x) => x.id === it.conflicts_with);
            return (
              <Item key={it.id} it={it} tone="suggested">
                <span className="ab-kind">Conflict · {it.label}</span>
                <span className="ab-text">New: {it.text}</span>
                <span className="ab-text ab-text--muted">On file: {old ? old.text : "the confirmed item"}</span>
                {it.source?.span ? <span className="ab-span">“{it.source.span}”</span> : null}
                <span className="ab-src">{sourceLine(it)}</span>
                <input id={`ab-cr-${it.id}`} className="ab-input" placeholder="Why? (kept on the record)"
                  value={reasons[it.id] || ""} onChange={(e) => setReasons({ ...reasons, [it.id]: e.target.value })} />
                <div className="ab-actions">
                  {["new", "old"].map((keep) => (
                    <button key={keep} type="button" className={`ab-btn ${keep === "new" ? "ab-btn--ok" : ""}`}
                      disabled={!!busy || !reasons[it.id]}
                      onClick={() => run(`c${it.id}`, () => resolveBriefConflict(it.id, { keep, reason: reasons[it.id] }),
                        keep === "new" ? "Replaced — old statement archived" : "Kept what's on file")}>
                      {keep === "new" ? "Keep new" : "Keep what's on file"}
                    </button>
                  ))}
                </div>
              </Item>
            );
          })}
          {toConfirm.map((it) => (
            <Item key={it.id} it={it} tone="suggested">
              <span className="ab-kind">AI suggested · {it.label}{it.promise?.code ? ` · ${it.promise.code}` : ""}</span>
              {editing[it.id] !== undefined ? (
                <textarea id={`ab-edit-${it.id}`} className="ab-input" rows={2} value={editing[it.id]}
                  onChange={(e) => setEditing({ ...editing, [it.id]: e.target.value })} />
              ) : (
                <span className="ab-text">{it.text}</span>
              )}
              {it.source?.span ? <span className="ab-span">“{it.source.span}”</span> : null}
              <span className="ab-src">{sourceLine(it)}</span>
              <div className="ab-actions">
                <button type="button" className="ab-btn ab-btn--ok" disabled={!!busy}
                  onClick={() => run(`k${it.id}`, () => confirmBriefItem(it.id, {
                    shown_at: shownAt(it.id), ...(editing[it.id] !== undefined ? { text: editing[it.id] } : {}),
                  }), "Confirmed — now visible to techs, voice and FieldRoutes")}>
                  {editing[it.id] !== undefined ? "Save & confirm" : "Confirm"}
                </button>
                {editing[it.id] === undefined ? (
                  <button type="button" className="ab-btn" disabled={!!busy}
                    onClick={() => setEditing({ ...editing, [it.id]: it.text })}>Edit</button>
                ) : null}
                <button type="button" className="ab-btn" disabled={!!busy}
                  onClick={() => run(`x${it.id}`, () => rejectBriefItem(it.id, { shown_at: shownAt(it.id) }), "Dismissed — won't be suggested again")}>
                  Not right
                </button>
              </div>
            </Item>
          ))}
        </section>
      )}

      {(hazards.length > 0 || doNots.length > 0 || (brief?.access_safety || []).length > 0) && (
        <section className="ab-sec">
          <div className="ab-sec-head"><h4>Safety & DO NOT</h4><span className="ab-count">always shown</span></div>
          {hazards.map((it) => (
            <Item key={it.id} it={it} tone="hazard">
              <span className="ab-text">⚠ {it.text}</span>
              {confirmedLine(it)}
              {stillTrue(it)}
            </Item>
          ))}
          {doNots.map((it) => (
            <Item key={it.id} it={it} tone="donot">
              <span className="ab-kind ab-kind--donot">DO NOT</span>
              <span className="ab-text">{it.text}</span>
              {confirmedLine(it)}
              {stillTrue(it)}
            </Item>
          ))}
          {(brief?.access_safety || []).map((f) => (
            <Item key={`f${f.fact_id}`} it={f} tone={f.fact_type === "hazard" ? "hazard" : ""}>
              <span className="ab-text">
                {f.fact_type.replace(/_/g, " ")}: {f.masked && !revealed[f.fact_id] ? <span className="ab-mask">{f.display_value}</span> : (revealed[f.fact_id] || f.display_value)}
                {f.masked && !revealed[f.fact_id] ? (
                  <button type="button" className="ab-btn ab-btn--inline" disabled={!!busy}
                    onClick={() => run(`r${f.fact_id}`, async () => {
                      const { data } = await revealAccessSafetyFact(f.fact_id);
                      setRevealed((r) => ({ ...r, [f.fact_id]: data?.value }));
                    })}>Reveal</button>
                ) : null}
              </span>
              <span className="ab-src">{f.confirmed_label}{f.stale ? " · unverified — check on site" : ""}{f.masked ? " · reveals are logged" : ""}</span>
            </Item>
          ))}
        </section>
      )}

      {promises.length > 0 && (
        <section className="ab-sec">
          <div className="ab-sec-head">
            <h4>Promises</h4>
            <span className="ab-count">{promises.filter((p) => p.promise?.status === "open").length} open</span>
          </div>
          {promises.map((it) => {
            const p = it.promise || {};
            const open = p.status === "open";
            return (
              <Item key={it.id} it={it} tone={p.overdue_days ? "overdue" : ""}>
                <div className="ab-row">
                  <span className="ab-text"><b>{p.code}</b> {it.text}</span>
                  <span className={`ab-chip ab-chip--${open ? (p.overdue_days ? "overdue" : "open") : "done"}`}>
                    {open ? (p.overdue_days ? `Overdue ${p.overdue_days}d` : p.due_at ? `Open · due ${fmtDate(p.due_at)}` : "Open") : p.status}
                  </span>
                </div>
                <span className="ab-src">
                  Owner: {ownerLabel(p.owner)} · promised by {sourceLine(it)} · {it.confirmed_label}
                </span>
                {(p.attempts || []).length ? <span className="ab-src">Couldn't do: {p.attempts[p.attempts.length - 1].why}</span> : null}
                {(p.misses || []).length ? <span className="ab-src ab-src--warn">Not addressed at the {p.misses[p.misses.length - 1].visit_date} visit</span> : null}
                {p.proposed_resolution ? (
                  <span className="ab-span">FieldRoutes note says {p.proposed_resolution.outcome === "kept" ? "KEPT" : "couldn't do"}: “{p.proposed_resolution.evidence?.span}”</span>
                ) : null}
                {open ? (
                  <div className="ab-actions">
                    <button type="button" className="ab-btn ab-btn--ok" disabled={!!busy}
                      onClick={() => run(`p${it.id}`, () => resolveBriefPromise(it.id, { outcome: "kept", evidence: { source_type: "office" } }), `${p.code} marked kept`)}>
                      Mark kept
                    </button>
                    <button type="button" className="ab-btn" disabled={!!busy}
                      onClick={() => run(`p${it.id}`, () => resolveBriefPromise(it.id, { outcome: "cancelled" }), `${p.code} closed`)}>
                      Close — no longer needed
                    </button>
                  </div>
                ) : null}
              </Item>
            );
          })}
        </section>
      )}

      {Object.keys(SECTION_TITLE).filter((s) => bySection[s]?.length).map((s) => (
        <section className="ab-sec" key={s}>
          <div className="ab-sec-head"><h4>{SECTION_TITLE[s]}</h4></div>
          {bySection[s].map((it) => (
            <Item key={it.id} it={it}>
              <span className="ab-text"><span className="ab-label">{it.label}:</span> {it.text}</span>
              {confirmedLine(it)}
              {it.confirmed_label?.startsWith("Auto-confirmed") ? (
                <div className="ab-actions">
                  <button type="button" className="ab-btn" disabled={!!busy}
                    onClick={() => run(`u${it.id}`, () => undoAutoBriefItem(it.id, {}), "Auto-confirm undone")}>Undo</button>
                </div>
              ) : null}
              {stillTrue(it)}
              {!it.still_true_due && !it.confirmed_label?.startsWith("Auto-confirmed") ? (
                <details className="ab-more">
                  <summary>No longer true?</summary>
                  <input id={`ab-rt-${it.id}`} className="ab-input" placeholder="What changed? (kept on the record)"
                    value={reasons[it.id] || ""} onChange={(e) => setReasons({ ...reasons, [it.id]: e.target.value })} />
                  <button type="button" className="ab-btn" disabled={!!busy || !reasons[it.id]}
                    onClick={() => run(`t${it.id}`, () => retractBriefItem(it.id, { reason: reasons[it.id] }), "Archived with your reason")}>
                    Archive
                  </button>
                </details>
              ) : null}
            </Item>
          ))}
        </section>
      ))}

      {brief?.money && brief.money.source !== "none" ? (
        <section className="ab-sec">
          <div className="ab-sec-head"><h4>Money</h4><span className="ab-count">office only · never shown to techs</span></div>
          <div className="ab-money">
            <span>Balance <b>${Number(brief.money.outstanding_balance || 0).toFixed(2)}</b></span>
            {brief.money.last_payment ? <span>Last paid {fmtDate(brief.money.last_payment.date)} · ${Number(brief.money.last_payment.amount || 0).toFixed(2)}</span> : null}
            {brief.money.as_of ? <span className="ab-src">as of {fmtDate(brief.money.as_of)} ({brief.money.source})</span> : null}
          </div>
        </section>
      ) : null}

      {brief && !items.length && !toConfirm.length && !conflicts.length && !(brief.access_safety || []).length ? (
        <p className="ab-empty">Nothing confirmed yet. Suggestions from calls, texts and tech notes appear here for you to confirm.</p>
      ) : null}

      <div className="ab-add">
        {adding ? (
          <div className="ab-add-form">
            <select id="ab-add-field" className="ab-input" value={draft.field_key}
              onChange={(e) => setDraft({ ...draft, field_key: e.target.value })}>
              <option value="">What kind of note?</option>
              {fields.filter((f) => !["history_summary"].includes(f.key)).map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
            <textarea id="ab-add-text" className="ab-input" rows={2} placeholder="Say it the way a tech should read it"
              value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
            <div className="ab-actions">
              <button type="button" className="ab-btn ab-btn--ok" disabled={!!busy || !draft.field_key || !draft.text.trim()}
                onClick={() => run("add", () => addBriefItem(customerId, draft), "Added — confirmed by you").then(() => {
                  setDraft({ field_key: "", text: "" }); setAdding(false);
                })}>
                Add
              </button>
              <button type="button" className="ab-btn" onClick={() => setAdding(false)}>Cancel</button>
            </div>
          </div>
        ) : (
          <button type="button" className="ab-btn" onClick={() => setAdding(true)}>+ Add to brief</button>
        )}
      </div>
      </>)}
    </div>
  );
}
